import React, { useState, useRef } from 'react';
import { useAppSelector } from '../store';
import { useNavigate } from 'react-router-dom';
import { Upload, CheckCircle2, ChevronRight, AlertCircle, ArrowLeft } from 'lucide-react';
import { apiGet, apiPost, apiPatch } from '../utils/api';
import { formatDistance as formatGlobalDistance, formatDuration as formatGlobalDuration } from '../utils/formatters';
import { trackGAEvent } from '../utils/analytics';

export const UploadPage: React.FC = () => {
  const token = useAppSelector((state) => state.auth.token);
  const user = useAppSelector((state) => state.auth.user);
  const navigate = useNavigate();

  const [dragActive, setDragActive] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<any | null>(null);
  const [selectedActivity, setSelectedActivity] = useState<string>('running');
  const [activityName, setActivityName] = useState<string>('');
  const [maxFileSizeMb, setMaxFileSizeMb] = useState<number>(20);

  const fileInputRef = useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    apiGet('/api/tracks/config')
      .then((data) => {
        if (data && typeof data.maxFileSizeMb === 'number') {
          setMaxFileSizeMb(data.maxFileSizeMb);
        }
      })
      .catch((err) => console.error('Error fetching track config:', err));
  }, []);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      validateAndSetFile(e.target.files[0]);
    }
  };

  const validateAndSetFile = (selectedFile: File) => {
    const ext = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();
    const allowed = ['.gpx', '.tcx', '.fit', '.zip'];
    if (!allowed.includes(ext)) {
      setError(`Unsupported file extension. Allowed: ${allowed.join(', ')}`);
      setFile(null);
      return;
    }

    if (selectedFile.size > maxFileSizeMb * 1024 * 1024) {
      setError(`File size exceeds limit of ${maxFileSizeMb}MB`);
      setFile(null);
      return;
    }

    setError(null);
    setFile(selectedFile);
  };

  const handleUpload = async () => {
    if (!file || !token) return;
    setLoading(true);
    setError(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const data = await apiPost('/api/tracks/upload', formData);
      setUploadResult(data);
      setSelectedActivity(data.activityType);
      setActivityName(data.name || '');

      const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase().replace('.', '');
      trackGAEvent('upload_track', {
        file_type: fileExt,
        points_count: data.pointsCount || 0,
        distance_km: data.distanceMeters ? (data.distanceMeters / 1000) : 0,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to upload and parse track');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!uploadResult) return;
    setLoading(true);

    try {
      // Save activity overrides (type and name) if changed
      const patchData: any = {};
      if (selectedActivity !== uploadResult.activityType) {
        patchData.activityType = selectedActivity;
      }
      if (activityName.trim() && activityName !== uploadResult.name) {
        patchData.name = activityName;
      }

      if (Object.keys(patchData).length > 0) {
        await apiPatch(`/api/tracks/${uploadResult.trackId}`, patchData);
      }
      navigate(`/preview/${uploadResult.trackId}`);
    } catch (err: any) {
      setError(err.message || 'Failed to confirm track details');
    } finally {
      setLoading(false);
    }
  };

  const formatDistance = (meters: number) => {
    return formatGlobalDistance(meters, user?.units || 'metric');
  };

  const formatDuration = (seconds: number) => {
    return formatGlobalDuration(seconds);
  };

  return (
    <div style={{ padding: '40px 24px', maxWidth: '800px', margin: '0 auto', width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '32px' }}>
        <button onClick={() => navigate('/library')} className="btn btn-secondary" style={{ padding: '8px 12px' }}>
          <ArrowLeft size={18} />
        </button>
        <h1 style={{ fontSize: '28px', margin: 0 }}>Upload Activity</h1>
      </div>

      {error && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '16px',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            borderRadius: '12px',
            color: 'var(--status-error)',
            marginBottom: '24px',
          }}
        >
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {!uploadResult ? (
        <div className="glass-panel" style={{ padding: '40px', textAlign: 'center' }}>
          <div
            onDragEnter={handleDrag}
            onDragOver={handleDrag}
            onDragLeave={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: `2px dashed ${dragActive ? 'var(--accent-primary)' : 'var(--border-glass)'}`,
              borderRadius: '12px',
              padding: '60px 40px',
              cursor: 'pointer',
              background: dragActive ? 'rgba(255, 56, 92, 0.04)' : 'rgba(0, 0, 0, 0.2)',
              transition: 'all 0.2s ease',
            }}
          >
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleChange}
              accept=".gpx,.tcx,.fit,.zip"
            />

            <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
              <div
                style={{
                  padding: '16px',
                  background: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: '50%',
                  color: dragActive ? 'var(--accent-primary)' : 'var(--text-secondary)',
                }}
              >
                <Upload size={36} />
              </div>
            </div>

            {file ? (
              <div>
                <p style={{ fontSize: '18px', fontWeight: 600, color: '#fff', marginBottom: '8px' }}>{file.name}</p>
                <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                  Ready to upload • {(file.size / 1024).toFixed(1)} KB
                </p>
              </div>
            ) : (
              <div>
                <p style={{ fontSize: '18px', fontWeight: 600, color: '#fff', marginBottom: '8px' }}>
                  Drag & drop your file here
                </p>
                <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                  Supports GPX, TCX, or FIT formats (max {maxFileSizeMb}MB)
                </p>
              </div>
            )}
          </div>

          {file && (
            <div style={{ marginTop: '32px', display: 'flex', justifyContent: 'center', gap: '16px' }}>
              <button onClick={() => setFile(null)} className="btn btn-secondary" disabled={loading}>
                Remove File
              </button>
              <button onClick={handleUpload} className="btn btn-primary" disabled={loading}>
                {loading ? <div className="spinner" style={{ width: '20px', height: '20px' }} /> : 'Upload and Parse'}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="glass-panel" style={{ padding: '40px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              color: 'var(--status-success)',
              marginBottom: '32px',
            }}
          >
            <CheckCircle2 size={28} />
            <h2 style={{ fontSize: '22px', margin: 0 }}>Activity Parsed Successfully!</h2>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '24px',
              marginBottom: '32px',
            }}
          >
            <div
              style={{
                padding: '16px',
                background: 'rgba(255, 255, 255, 0.02)',
                borderRadius: '8px',
                border: '1px solid var(--border-glass)',
              }}
            >
              <span style={{ fontSize: '13px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                Distance
              </span>
              <strong style={{ fontSize: '20px', color: '#fff' }}>{formatDistance(uploadResult.totalDistance)}</strong>
            </div>
            <div
              style={{
                padding: '16px',
                background: 'rgba(255, 255, 255, 0.02)',
                borderRadius: '8px',
                border: '1px solid var(--border-glass)',
              }}
            >
              <span style={{ fontSize: '13px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                Duration
              </span>
              <strong style={{ fontSize: '20px', color: '#fff' }}>{formatDuration(uploadResult.totalDuration)}</strong>
            </div>
            <div
              style={{
                padding: '16px',
                background: 'rgba(255, 255, 255, 0.02)',
                borderRadius: '8px',
                border: '1px solid var(--border-glass)',
              }}
            >
              <span style={{ fontSize: '13px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                Elevation Gain
              </span>
              <strong style={{ fontSize: '20px', color: '#fff' }}>
                +{Math.round(uploadResult.totalElevationGain)}m
              </strong>
            </div>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '24px',
              marginBottom: '40px',
            }}
          >
            <div className="input-group" style={{ marginBottom: 0 }}>
              <label className="input-label">Activity Name</label>
              <input
                type="text"
                className="text-input"
                style={{ background: 'rgba(0, 0, 0, 0.3)' }}
                value={activityName}
                onChange={(e) => setActivityName(e.target.value)}
                disabled={loading}
                placeholder="E.g. Afternoon Ride"
              />
            </div>

            <div className="input-group" style={{ marginBottom: 0 }}>
              <label className="input-label">Override Activity Type</label>
              <select
                className="text-input"
                style={{ background: 'rgba(0, 0, 0, 0.3)', cursor: 'pointer' }}
                value={selectedActivity}
                onChange={(e) => setSelectedActivity(e.target.value)}
                disabled={loading}
              >
                <option value="running">Running 🏃</option>
                <option value="cycling">Cycling 🚴</option>
                <option value="hiking">Hiking 🥾</option>
                <option value="climbing">Climbing 🧗</option>
                <option value="other">Other 📦</option>
              </select>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginTop: '4px' }}>
                System auto-detected activity as:{' '}
                <strong style={{ textTransform: 'capitalize' }}>{uploadResult.activityType}</strong>
              </span>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '16px',
              borderTop: '1px solid var(--border-glass)',
              paddingTop: '24px',
            }}
          >
            <button onClick={() => setUploadResult(null)} className="btn btn-secondary" disabled={loading}>
              Upload Another
            </button>
            <button onClick={handleConfirm} className="btn btn-primary" disabled={loading}>
              {loading ? (
                <div className="spinner" style={{ width: '20px', height: '20px' }} />
              ) : (
                <>
                  <span>Continue to Preview</span>
                  <ChevronRight size={18} />
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
